/**
 * Level Card Component
 * Shows current level with badge, XP progress, and next rewards
 */

import { useMemo } from 'react';
import { Medal, Award, Star, Trophy, Diamond } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { LevelInfo, XpProgress, LevelIconType } from '../types';
import { formatXp } from '../utils/helpers';

// Static icon components - defined outside component
const LEVEL_ICONS = {
  medal: Medal,
  award: Award,
  star: Star,
  trophy: Trophy,
  diamond: Diamond,
} as const;

interface LevelCardProps {
  level: number;
  currentLevelInfo: LevelInfo | null;
  nextLevelInfo: LevelInfo | null;
  xpProgress: XpProgress;
  getLevelIcon: () => LevelIconType;
}

export function LevelCard({
  level,
  currentLevelInfo,
  nextLevelInfo,
  xpProgress,
  getLevelIcon,
}: LevelCardProps) {
  const { t } = useLanguage();

  // Get icon component based on level
  const iconType = useMemo(() => getLevelIcon(), [getLevelIcon]);
  const LevelIcon = LEVEL_ICONS[iconType];

  return (
    <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
      <div className="flex items-center gap-6">
        {/* Level Badge */}
        <div className="relative flex-shrink-0">
          <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
            <LevelIcon className="w-12 h-12" />
          </div>
          {level > 0 && (
            <span className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-yellow-400 text-yellow-900 font-bold text-lg rounded-full border-4 border-white shadow">
              {level}
            </span>
          )}
        </div>

        {/* Level Info */}
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-bold">
            {t('profile.level', 'Level')} {level} - {currentLevelInfo?.title || t('profile.newbie', 'Newbie')}
          </div>
          <div className="text-white/80 mt-1">
            {formatXp(xpProgress.current + (currentLevelInfo?.cumulativeXp || 0) - (currentLevelInfo?.xpRequired || 0))} {t('profile.xpTotal', 'XP total')}
          </div>

          {/* Progress bar */}
          {level < 10 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-white/70 mb-1">
                <span>{formatXp(xpProgress.current)} XP</span>
                <span>{formatXp(xpProgress.required)} XP</span>
              </div>
              <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                  style={{ width: `${xpProgress.percentage}%` }}
                />
              </div>
              {nextLevelInfo && (
                <div className="text-sm text-white/70 mt-2">
                  {t('profile.nextLevel', 'Next level')}: {nextLevelInfo.title}
                  {nextLevelInfo.badgeId && ` + ${t('profile.badge', 'Badge')}`}
                  {nextLevelInfo.watchlistSlotReward && ` + ${t('profile.watchlistSlot', 'Watchlist Slot')}`}
                  {nextLevelInfo.premiumDaysReward && ` + ${nextLevelInfo.premiumDaysReward} ${t('profile.proDay', 'Pro Day')}`}
                </div>
              )}
            </div>
          )}

          {level >= 10 && (
            <div className="mt-4 text-lg font-semibold text-yellow-400">
              {t('profile.maxLevelAchieved', 'Maximum Level Achieved!')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
